
import React, { useMemo } from 'react';
import { View, UserRole, SchoolSettings } from '../types.ts';
import { viewColors } from '../constants.ts';
import { APP_THEMES, getThemeById } from '../src/themeConfig.ts';
import { 
  LayoutDashboard, 
  Users, 
  NotebookPen, 
  CalendarCheck, 
  BookMarked,
  CalendarClock,
  Clock,
  CalendarDays,
  GraduationCap,
  Layers,
  UserCog,
  Building2,
  X,
  Target,
  User as UserIcon,
  Sparkles,
  BarChart3,
  Database as DatabaseIcon,
  ClipboardList,
  History,
  Award,
  UserCheck
} from 'lucide-react';

interface SidebarProps {
  role: UserRole;
  activeView: View;
  setActiveView: (view: View) => void;
  isSidebarOpen: boolean;
  setSidebarOpen: (isOpen: boolean) => void;
  isDarkMode: boolean;
  schoolSettings: SchoolSettings;
  appTheme?: string;
}

const Sidebar: React.FC<SidebarProps> = ({ role, activeView, setActiveView, isSidebarOpen, setSidebarOpen, isDarkMode, schoolSettings, appTheme = 'wzc-themes' }) => {
  const currentTheme = useMemo(() => getThemeById(appTheme), [appTheme]);
  const adminItems = [
    { view: View.Dashboard, icon: <LayoutDashboard size={20} />, color: 'text-blue-400' },
    { view: View.ManageTeachers, icon: <UserCog size={20} />, color: 'text-indigo-400' },
    { view: View.ManageClasses, icon: <Layers size={20} />, color: 'text-purple-400' },
    { view: View.ManageStudents, icon: <Users size={20} />, color: 'text-emerald-400' },
    { view: View.LearningObjectives, icon: <Target size={20} />, color: 'text-pink-400', label: 'Tujuan Pembelajaran' },
    { view: View.AdminAgenda, icon: <BookMarked size={20} />, color: 'text-orange-400', label: 'Agenda Guru' },
    { view: View.Schedule, icon: <CalendarDays size={20} />, color: 'text-sky-400', label: 'Kalender Pendidikan' },
    { view: View.Settings, icon: <Building2 size={20} />, color: 'text-slate-400' },
    { view: View.PeriodSettings, icon: <CalendarClock size={20} />, color: 'text-amber-400' },
    { view: View.Database, icon: <DatabaseIcon size={20} />, color: 'text-rose-400' },
    { view: View.ProfileAccessHistory, icon: <History size={20} />, color: 'text-cyan-400' },
  ];

  const guruItems = [
    { view: View.Dashboard, icon: <LayoutDashboard size={20} />, color: 'text-blue-400' },
    { view: View.Schedule, icon: <CalendarDays size={20} />, color: 'text-sky-400' },
    { view: View.LearningObjectives, icon: <Target size={20} />, color: 'text-pink-400' },
    { view: View.Perwalian, icon: <UserCheck size={20} />, color: 'text-emerald-400', label: 'Administrasi Walas' },
    { view: View.Agenda, icon: <BookMarked size={20} />, color: 'text-orange-400' },
    { view: View.Attendance, icon: <CalendarCheck size={20} />, color: 'text-teal-400' },
    { view: View.Grading, icon: <NotebookPen size={20} />, color: 'text-lime-400' },
    { view: View.NilaiRapor, icon: <BarChart3 size={20} />, color: 'text-violet-400' },
    { view: View.MaterialGenerator, icon: <Sparkles size={20} />, color: 'text-yellow-400' },
    { view: View.Tugas, icon: <ClipboardList size={20} />, color: 'text-fuchsia-400' },
    { view: View.StudentList, icon: <Users size={20} />, color: 'text-green-400' },
    { view: View.ProfileAccessHistory, icon: <History size={20} />, color: 'text-cyan-400' },
  ];

  const siswaItems = [
    { view: View.StudentProfile, icon: <UserIcon size={20} />, color: 'text-indigo-400', label: 'Profil Saya' },
    { view: View.StudentGrade, icon: <Award size={20} />, color: 'text-amber-400', label: 'Nilai Saya' },
    { view: View.Tugas, icon: <ClipboardList size={20} />, color: 'text-fuchsia-400' },
    { view: View.SubjectSelection, icon: <BookMarked size={20} />, color: 'text-blue-400' },
  ];

  const navItems = role === UserRole.Admin ? adminItems : role === UserRole.Guru ? guruItems : siswaItems;

  const NavLink: React.FC<{ item: typeof navItems[0] }> = ({ item }) => {
    const isStudentItem = role === UserRole.Siswa;
    const isMaintenance = schoolSettings.isStudentMaintenanceMode;
    const isDisabled = isStudentItem && isMaintenance;
    const isActive = activeView === item.view;

    return (
      <button
        onClick={() => {
          if (isDisabled) return;
          setActiveView(item.view)
          if (window.innerWidth < 1024) {
            setSidebarOpen(false);
          }
        }}
        disabled={isDisabled}
        className={`flex items-center w-full px-4 py-3 text-sm font-medium transition-all rounded-xl ${
          isActive
            ? `${currentTheme.sidebarActive} shadow-md font-bold`
            : isDisabled
            ? 'opacity-40 cursor-not-allowed text-slate-400 dark:text-slate-600'
            : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800'
        }`}
      >
        <span className={`mr-3 transition-colors ${isActive ? 'text-white' : isDisabled ? 'text-slate-400 dark:text-slate-600' : item.color}`}>
          {item.icon}
        </span>
        <span>{(item as any).label || item.view}</span>
      </button>
    );
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-sm transition-opacity lg:hidden ${
          isSidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setSidebarOpen(false)}
      />
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex-shrink-0 w-64 px-4 py-6 overflow-y-auto h-full transition-transform duration-300 lg:relative lg:translate-x-0 ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } app-sidebar bg-white dark:bg-black text-slate-900 dark:text-white border-r border-slate-200 dark:border-white/10`}
      >
        <div className="flex items-center justify-between mb-8 px-2">
          <div className="flex items-center gap-3">
            {schoolSettings.logoUrl ? (
              <img src={schoolSettings.logoUrl} alt="Logo" className="w-10 h-10 object-contain bg-white rounded-lg p-1" referrerPolicy="no-referrer" />
            ) : (
              <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
                <GraduationCap size={24} />
              </div>
            )}
            <div className="overflow-hidden">
              <h1 className="text-sm font-bold truncate">{schoolSettings.schoolName || 'Portal Sekolah'}</h1>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">{role}</p>
            </div>
          </div>
          <button className="lg:hidden p-1 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white" onClick={() => setSidebarOpen(false)}>
            <X size={20} />
          </button>
        </div>

        <nav className="space-y-1">
          {navItems.map((item) => (
            <NavLink key={item.view} item={item} />
          ))}
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
